import { useSyncExternalStore, useEffect } from 'react';
import { salaryModuleApi } from '../services/salary-module';
import type { BobtailQuote } from '../types/salary-module';

/**
 * 司机卡上那条 BT 徽标要显示的钱。
 *
 * 为什么是一个模块级的小仓库，而不是普通的 useEffect 取数：
 * 一屏十几张司机卡、每张卡好几段空驶，每段都是一次独立的问价。挨个发请求就是
 * 几十个并发；提到容器层去统一取，又得把「这张卡排到第几位、上一程收在哪」这
 * 些只有卡片自己知道的信息一路传上去。所以这里让每段自己报名，同一帧里报上来
 * 的合成一个请求发出去，答案进缓存，所有订阅者一起重渲染。
 *
 * 拖动排序会不停生成新的组合，所以缓存只增不减（键是司机+两个区域，值域很小，
 * 一个租户撑死几百条），已经问过的组合再拖回来是立即命中，不闪。
 */

type QuoteKey = string;

const keyOf = (driverId: number, fromZoneId: number, toZoneId: number): QuoteKey =>
  `${driverId}|${fromZoneId}|${toZoneId}`;

const cache = new Map<QuoteKey, BobtailQuote | null>();
const inflight = new Set<QuoteKey>();
const pending = new Map<QuoteKey, { driverId: number; fromZoneId: number; toZoneId: number }>();
const listeners = new Set<() => void>();

let flushScheduled = false;

function emit() {
  listeners.forEach(fn => fn());
}

function scheduleFlush() {
  if (flushScheduled) return;
  flushScheduled = true;
  // 等这一帧的卡片全部渲染完再发 —— 一屏几十段合成一个请求。
  setTimeout(flush, 0);
}

async function flush() {
  flushScheduled = false;
  if (pending.size === 0) return;

  const batch = Array.from(pending.entries());
  pending.clear();
  batch.forEach(([key]) => inflight.add(key));

  try {
    const quotes = await salaryModuleApi.quoteBobtail(batch.map(([, pair]) => pair));
    const byKey = new Map(quotes.map(q => [keyOf(q.driverId, q.fromZoneId, q.toZoneId), q]));
    batch.forEach(([key]) => cache.set(key, byKey.get(key) ?? null));
  } catch {
    // 问价失败不该把整张卡搞坏 —— 徽标退回只显示里程，下次进板子再问。
    // 写 null 会被当成「问过了，没有价」，所以这里什么都不写，只把在途清掉。
    batch.forEach(([key]) => cache.delete(key));
  } finally {
    batch.forEach(([key]) => inflight.delete(key));
    emit();
  }
}

function request(driverId: number, fromZoneId: number, toZoneId: number) {
  const key = keyOf(driverId, fromZoneId, toZoneId);
  if (cache.has(key) || inflight.has(key) || pending.has(key)) return;
  pending.set(key, { driverId, fromZoneId, toZoneId });
  scheduleFlush();
}

function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => { listeners.delete(listener); };
}

/** 费率卡改了之后调一下，下次渲染会重新问价。 */
export function clearBobtailQuoteCache() {
  cache.clear();
  emit();
}

/**
 * 一段空驶的报价。参数缺任何一个（没司机、区域没配）都返回 undefined —— 徽标
 * 照常显示里程，只是不带钱。
 *
 * 返回 `undefined` = 还没答案（在问，或者问不了）；
 * 返回的对象里 `amount === null` = 问过了，这段路一档都没配费率。
 */
export function useBobtailQuote(
  driverId?: number | null,
  fromZoneId?: number | null,
  toZoneId?: number | null,
): BobtailQuote | null | undefined {
  const usable = !!driverId && !!fromZoneId && !!toZoneId;
  const key = usable ? keyOf(driverId!, fromZoneId!, toZoneId!) : '';

  useEffect(() => {
    if (usable) request(driverId!, fromZoneId!, toZoneId!);
  }, [usable, driverId, fromZoneId, toZoneId]);

  return useSyncExternalStore(
    subscribe,
    () => (usable ? cache.get(key) : undefined),
    () => undefined,
  );
}
